import { useState, useEffect, useRef } from "react";
import { getCachedImage, setCachedImage, deleteCachedImage } from "../utils/imageCache";
import { supabase } from "../utils/supabaseClient";

interface UsePersistentImageProps {
  url: string;
  lastModified: string;
}

interface MemoryImage {
  blob: Blob;
  lastModified: string;
}

const MAX_MEMORY_CACHE_ITEMS = 60;
const memoryCache = new Map<string, MemoryImage>();
const inFlightFetches = new Map<string, Promise<Blob>>();

const rememberImage = (url: string, blob: Blob, lastModified: string) => {
  memoryCache.delete(url);
  memoryCache.set(url, { blob, lastModified });

  if (memoryCache.size > MAX_MEMORY_CACHE_ITEMS) {
    const oldestUrl = memoryCache.keys().next().value;
    if (oldestUrl) memoryCache.delete(oldestUrl);
  }
};

const fetchImageBlob = async (url: string, lastModified: string): Promise<Blob> => {
  const fetchKey = `${url}:${lastModified}`;
  const existingFetch = inFlightFetches.get(fetchKey);
  if (existingFetch) return existingFetch;

  const fetchPromise = (async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    return response.blob();
  })();

  inFlightFetches.set(fetchKey, fetchPromise);

  try {
    return await fetchPromise;
  } finally {
    inFlightFetches.delete(fetchKey);
  }
};

export function usePersistentImage({ url, lastModified }: UsePersistentImageProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const currentBlobUrlRef = useRef<string | null>(null);

  const cleanup = () => {
    // Cleanup previous blob URL to prevent memory leaks
    if (currentBlobUrlRef.current) {
      URL.revokeObjectURL(currentBlobUrlRef.current);
      currentBlobUrlRef.current = null;
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadImage = async () => {
      if (!url) {
        setLoading(false);
        setError('Missing URL');
        return;
      }

      setLoading(true);
      setError(null);
      cleanup();
      setImageUrl(null);

      try {
        const memoryImage = memoryCache.get(url);
        if (memoryImage && memoryImage.lastModified === lastModified) {
          if (mounted) {
            const localUrl = URL.createObjectURL(memoryImage.blob);
            currentBlobUrlRef.current = localUrl;
            setImageUrl(localUrl);
            setLoading(false);
          }
          return;
        }

        // Check IndexedDB first
        const cached = await getCachedImage(url);

        // If cached and same last_modified, use it
        if (cached && cached.lastModified === lastModified) {
          rememberImage(url, cached.blob, lastModified);
          if (mounted) {
            const localUrl = URL.createObjectURL(cached.blob);
            currentBlobUrlRef.current = localUrl;
            setImageUrl(localUrl);
            setLoading(false);
          }
          return;
        }

        // If different last_modified or not cached, delete old cache and fetch new
        if (cached && cached.lastModified !== lastModified) {
          deleteCachedImage(url).catch(() => {});
        }

        // Fetch from server
        const blob = await fetchImageBlob(url, lastModified);
        rememberImage(url, blob, lastModified);

        if (mounted) {
          const blobUrl = URL.createObjectURL(blob);
          currentBlobUrlRef.current = blobUrl;
          setImageUrl(blobUrl);
          setLoading(false);
        }

        setCachedImage(url, blob, lastModified).catch(() => {});
        
      } catch (err) {
        console.error("Image load error:", err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load image');
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
      cleanup();
    };
  }, [url, lastModified]);

  return { imageUrl, loading, error };
}
